import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError, toApiError, unwrap, type Schemas } from "../../api/client";
import { ErrorMessage, LiveMessage, Loading, Notice } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { formatDateTime, formatNumber, toDateTimeInput } from "../../lib/format";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { FauModeration } from "./FauModeration";
import { useKommune } from "./KommuneContext";

type Campaign = Schemas["CampaignOut"];
type School = { school_id: string; name: string };

const STATUS_KEYS: Record<Campaign["status"], MessageKey> = {
  planlagt: "fau.statusPlanlagt",
  åpen: "fau.statusApen",
  stengt: "fau.statusStengt",
  trukket: "fau.statusTrukket",
};

/** Same-origin URL: a plain `<img>`/download link keeps the session cookie. */
export function fauQrUrl(orgId: string, nr: string, campaignId: string): string {
  return `/api/orgs/${orgId}/kommuner/${nr}/fau/campaigns/${campaignId}/qr.png`;
}

interface FormValues {
  title: string;
  schoolId: string;
  opens: string;
  closes: string;
  dailyCap: string;
}

function CampaignForm({
  heading,
  submitLabel,
  schools,
  initial,
  schoolLocked,
  capHint,
  onSubmit,
  onCancel,
}: {
  heading: string;
  submitLabel: string;
  schools: readonly School[];
  initial: FormValues;
  schoolLocked: boolean;
  capHint: MessageKey;
  onSubmit: (values: FormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const id = useId();
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [periodError, setPeriodError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  // Move focus into the form when it replaces the button or card that opened it.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const set = (field: keyof FormValues) => (value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // `required` lets a whitespace-only title through; the backend would answer 422.
    const badTitle = values.title.trim() === "";
    setTitleError(badTitle);
    if (badTitle) {
      titleRef.current?.focus();
      return;
    }
    // datetime-local values are fixed-width `YYYY-MM-DDTHH:mm`, so string order is time order.
    const badPeriod = !!values.opens && !!values.closes && values.closes <= values.opens;
    setPeriodError(badPeriod);
    if (badPeriod) return;
    setBusy(true);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={(e) => void submit(e)} aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`}>{heading}</h3>
      <div className="field">
        <label htmlFor={`${id}-title`}>{t("fau.fieldTitle")}</label>
        <input
          id={`${id}-title`}
          ref={titleRef}
          required
          maxLength={200}
          aria-invalid={titleError ? true : undefined}
          aria-describedby={titleError ? `${id}-title-error ${id}-title-hint` : `${id}-title-hint`}
          value={values.title}
          onChange={(e) => set("title")(e.target.value)}
        />
        {titleError && (
          <span id={`${id}-title-error`} className="field-error" role="alert">
            {t("fau.titleRequired")}
          </span>
        )}
        <span id={`${id}-title-hint`} className="hint">
          {t("fau.fieldTitleHint")}
        </span>
      </div>
      <div className="field">
        <label htmlFor={`${id}-school`}>{t("fau.fieldSchool")}</label>
        <select
          id={`${id}-school`}
          value={values.schoolId}
          disabled={schoolLocked}
          aria-describedby={schoolLocked ? `${id}-school-hint` : undefined}
          onChange={(e) => set("schoolId")(e.target.value)}
        >
          <option value="">{t("fau.allSchools")}</option>
          {schools.map((s) => (
            <option key={s.school_id} value={s.school_id}>
              {s.name}
            </option>
          ))}
        </select>
        {schoolLocked && (
          <span id={`${id}-school-hint`} className="hint">
            {t("fau.schoolLocked")}
          </span>
        )}
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor={`${id}-opens`}>{t("fau.fieldOpens")}</label>
          <input
            id={`${id}-opens`}
            type="datetime-local"
            required
            value={values.opens}
            onChange={(e) => set("opens")(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-closes`}>{t("fau.fieldCloses")}</label>
          <input
            id={`${id}-closes`}
            type="datetime-local"
            required
            value={values.closes}
            aria-invalid={periodError ? true : undefined}
            aria-describedby={periodError ? `${id}-closes-error` : undefined}
            onChange={(e) => set("closes")(e.target.value)}
          />
          {periodError && (
            <span id={`${id}-closes-error`} className="field-error" role="alert">
              {t("fau.closesBeforeOpens")}
            </span>
          )}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`${id}-cap`}>{t("fau.fieldDailyCap")}</label>
        <input
          id={`${id}-cap`}
          type="number"
          min={1}
          max={10000}
          className="input-short"
          aria-describedby={`${id}-cap-hint`}
          value={values.dailyCap}
          onChange={(e) => set("dailyCap")(e.target.value)}
        />
        <span id={`${id}-cap-hint`} className="hint">
          {t(capHint)}
        </span>
      </div>
      {error && <ErrorMessage error={error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t("common.working") : submitLabel}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

function initialValues(c: Campaign): FormValues {
  return {
    title: c.title,
    schoolId: c.school_id ?? "",
    opens: toDateTimeInput(c.opens_at),
    closes: toDateTimeInput(c.closes_at),
    dailyCap: String(c.daily_cap),
  };
}

function dailyCapValue(raw: string): number | undefined {
  return raw.trim() === "" ? undefined : Number(raw);
}

function CampaignCard({
  orgId,
  nr,
  campaign: c,
  schools,
  onChanged,
  onMessage,
}: {
  orgId: string;
  nr: string;
  campaign: Campaign;
  schools: readonly School[];
  onChanged: (c: Campaign) => void;
  onMessage: (msg: string) => void;
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const path = { org_id: orgId, nummer: nr, campaign_id: c.id };
  const revoked = c.status === "trukket";
  const initial = initialValues(c);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const refocusHeading = useRef(false);

  // Keyboard and screen reader users follow the UI as it swaps: into the confirmation when it
  // opens, back to the card heading when it (or the edit form) goes away.
  useEffect(() => {
    if (confirming) {
      confirmRef.current?.focus();
    } else if (!editing && refocusHeading.current) {
      refocusHeading.current = false;
      headingRef.current?.focus();
    }
  }, [confirming, editing]);

  function closeEditing() {
    refocusHeading.current = true;
    setEditing(false);
  }

  function closeConfirming() {
    refocusHeading.current = true;
    setConfirming(false);
  }

  async function save(v: FormValues) {
    // Send only what changed, so untouched times are not shifted by a round trip through the form
    // (seconds are dropped, and the repeated hour at the end of summer time is ambiguous).
    const body: Schemas["CampaignPatch"] = {};
    if (v.title.trim() !== initial.title) body.title = v.title.trim();
    if (v.opens !== initial.opens) body.opens_at = v.opens;
    if (v.closes !== initial.closes) body.closes_at = v.closes;
    if (v.dailyCap !== initial.dailyCap) body.daily_cap = dailyCapValue(v.dailyCap);
    if (Object.keys(body).length === 0) {
      closeEditing();
      return;
    }
    const updated = await unwrap(
      api.PATCH("/api/orgs/{org_id}/kommuner/{nummer}/fau/campaigns/{campaign_id}", {
        params: { path },
        body,
      }),
    );
    closeEditing();
    onChanged(updated);
    onMessage(t("fau.updated", { title: updated.title }));
  }

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      const updated = await unwrap(
        api.POST("/api/orgs/{org_id}/kommuner/{nummer}/fau/campaigns/{campaign_id}/revoke", {
          params: { path },
        }),
      );
      closeConfirming();
      onChanged(updated);
      onMessage(t("fau.revoked", { title: updated.title }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(c.link);
      onMessage(t("fau.copied"));
    } catch {
      onMessage(t("fau.copyFailed"));
    }
  }

  if (editing) {
    return (
      <li>
        <CampaignForm
          heading={t("fau.editTitle")}
          submitLabel={t("fau.save")}
          schools={
            c.school_id && !schools.some((s) => s.school_id === c.school_id)
              ? [...schools, { school_id: c.school_id, name: c.school_name ?? c.school_id }]
              : schools
          }
          schoolLocked
          initial={initial}
          capHint="fau.fieldDailyCapHintEdit"
          onSubmit={save}
          onCancel={closeEditing}
        />
      </li>
    );
  }

  return (
    <li>
      <article className="card" aria-labelledby={`${id}-title`}>
        <h3 id={`${id}-title`} ref={headingRef} tabIndex={-1}>
          {c.title} <span className="badge">{t(STATUS_KEYS[c.status])}</span>
        </h3>
        <dl className="details">
          <dt>{t("fau.fieldSchool")}</dt>
          <dd>{c.school_name ?? t("fau.allSchools")}</dd>
          <dt>{t("fau.periodLabel")}</dt>
          <dd>
            {t("fau.period", {
              opens: formatDateTime(c.opens_at),
              closes: formatDateTime(c.closes_at),
            })}
          </dd>
          <dt>{t("fau.moderationTitle")}</dt>
          <dd>
            {t("fau.counts", {
              pending: formatNumber(c.report_counts.pending),
              approved: formatNumber(c.report_counts.approved),
              rejected: formatNumber(c.report_counts.rejected),
            })}
          </dd>
        </dl>
        <p className="muted small">{t("fau.dailyCap", { cap: formatNumber(c.daily_cap) })}</p>
        {!revoked && (
          <div className="field">
            <label htmlFor={`${id}-link`}>{t("fau.link")}</label>
            <div className="input-row">
              <input id={`${id}-link`} readOnly value={c.link} onFocus={(e) => e.target.select()} />
              <button
                type="button"
                className="btn btn-secondary btn-small"
                aria-label={t("fau.copyLabel", { title: c.title })}
                onClick={() => void copy()}
              >
                {t("fau.copy")}
              </button>
              <a href={c.link} target="_blank" rel="noreferrer">
                {t("fau.openLink")}
              </a>
            </div>
          </div>
        )}
        <div className="link-group">
          {!revoked && (
            <a
              href={fauQrUrl(orgId, nr, c.id)}
              download={`fau-qr-${c.id}.png`}
              aria-label={t("fau.qrDownloadLabel", { title: c.title })}
            >
              {t("fau.qrDownload")}
            </a>
          )}
          {!revoked && !confirming && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                aria-label={t("fau.editLabel", { title: c.title })}
                onClick={() => setEditing(true)}
              >
                {t("fau.edit")}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                aria-label={t("fau.revokeLabel", { title: c.title })}
                onClick={() => setConfirming(true)}
              >
                {t("fau.revoke")}
              </button>
            </>
          )}
        </div>
        {confirming && (
          <Notice kind="warning" title={t("fau.revokeConfirmTitle")}>
            <p>{t("fau.revokeConfirmBody", { title: c.title })}</p>
            <div className="link-group">
              <button
                type="button"
                ref={confirmRef}
                className="btn btn-danger btn-small"
                disabled={busy}
                onClick={() => void revoke()}
              >
                {t("fau.revokeConfirm")}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={closeConfirming}
              >
                {t("common.cancel")}
              </button>
            </div>
          </Notice>
        )}
        {error && <ErrorMessage error={error} />}
      </article>
    </li>
  );
}

const EMPTY_FORM: FormValues = { title: "", schoolId: "", opens: "", closes: "", dailyCap: "" };

export function FauAdminPage() {
  const { orgId, nr, navn, overview } = useKommune();
  usePageTitle(`${t("fau.title")} – ${navn}`);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState("");
  const schools = overview?.schools ?? [];
  const campaigns = useQuery(`fau-campaigns:${orgId}:${nr}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/fau/campaigns", {
        params: { path: { org_id: orgId, nummer: nr } },
        signal,
      }),
    ),
  );

  async function create(v: FormValues) {
    const created = await unwrap(
      api.POST("/api/orgs/{org_id}/kommuner/{nummer}/fau/campaigns", {
        params: { path: { org_id: orgId, nummer: nr } },
        body: {
          title: v.title.trim(),
          school_id: v.schoolId || null,
          opens_at: v.opens,
          closes_at: v.closes,
          daily_cap: dailyCapValue(v.dailyCap),
        },
      }),
    );
    campaigns.setData((prev) => [created, ...(prev ?? [])]);
    setCreating(false);
    announce(t("fau.created", { title: created.title }));
  }

  /** Clear, then set: a live region stays silent when it gets the same string twice. */
  function announce(msg: string) {
    setMessage("");
    window.setTimeout(() => setMessage(msg), 50);
  }

  function replace(updated: Campaign) {
    campaigns.setData((prev) => prev?.map((c) => (c.id === updated.id ? updated : c)));
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>{t("fau.title")}</h1>
        <p>{t("fau.intro")}</p>
      </header>
      <LiveMessage message={message} />
      <section className="section" aria-labelledby="fau-campaigns-heading">
        <h2 id="fau-campaigns-heading">{t("fau.campaignsTitle")}</h2>
        {creating ? (
          <CampaignForm
            heading={t("fau.createTitle")}
            submitLabel={t("fau.create")}
            schools={schools}
            schoolLocked={false}
            initial={EMPTY_FORM}
            capHint="fau.fieldDailyCapHint"
            onSubmit={create}
            onCancel={() => setCreating(false)}
          />
        ) : (
          <p>
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              {t("fau.newCampaign")}
            </button>
          </p>
        )}
        {campaigns.error && <ErrorMessage error={campaigns.error} onRetry={campaigns.reload} />}
        {!campaigns.data && !campaigns.error && <Loading />}
        {campaigns.data?.length === 0 && <p>{t("fau.campaignsEmpty")}</p>}
        {!!campaigns.data?.length && (
          <ul className="plain-list">
            {campaigns.data.map((c) => (
              <CampaignCard
                key={c.id}
                orgId={orgId}
                nr={nr}
                campaign={c}
                schools={schools}
                onChanged={replace}
                onMessage={announce}
              />
            ))}
          </ul>
        )}
      </section>
      <FauModeration
        orgId={orgId}
        nr={nr}
        campaigns={campaigns.data ?? []}
        onModerated={campaigns.reload}
        onMessage={announce}
      />
    </div>
  );
}
